import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useStore } from "jotai";
import { useCallback } from "react";

import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../../../../../../utils/hooks/useMutationWithError";
import { updateGolfBookingPayloadAtom } from "../../../atoms/selectors/UpdatePayloads/updateGolfBookingPayload/updateGolfBookingPayloadAtom";
import { currentSessionMemberShipTypeIdAtom } from "../../../atoms/stores";
import type { PatronEditBooking } from "../../../types";
import { useSaveChangesToSingleSlotBookingMutation } from "./__generated__/useSaveChangesToSingleSlotBookingMutation.graphql";

export type RecurringRequestPreviewConfirmation = {
  reviewedPlanToken: string;
};

export const useSaveChangesToSingleSlotBooking = (
  successCallback: (options?: PatronEditBooking.SaveSuccessOptions) => void
) => {
  const { showToast } = useToast();
  const store = useStore();
  const { patronLoyalty } = useFlags();
  const [saveChangesToSingleSlotBooking, isSavingChanges] =
    useMutationWithError<useSaveChangesToSingleSlotBookingMutation>(
      useSaveChangesToSingleSlotBookingString
    );
  const membershipTypeId = useAtomValue(currentSessionMemberShipTypeIdAtom);
  const handleSaveSingleSlotBooking = useCallback(
    (
      editScope: "THIS_ONLY" | "THIS_AND_FOLLOWING" = "THIS_ONLY",
      previewConfirmation?: RecurringRequestPreviewConfirmation
    ) => {
      const updateGolfBookingPayload = store.get(updateGolfBookingPayloadAtom);
      if (!updateGolfBookingPayload) return;
      saveChangesToSingleSlotBooking({
        variables: {
          input: {
            ...updateGolfBookingPayload,
            editScope,
            ...(previewConfirmation ?? {}),
          },
          membershipTypeId: membershipTypeId ?? "",
          bookingType: "STANDARD",
          patronLoyalty: !!patronLoyalty,
        },
        onCompleted: (data) => {
          successCallback();
        },
        onCompletedErrorCustom: (errors) => {
          const committedWarning = errors.find(
            (error) =>
              error.code === "recurring_field_edit_post_commit_incomplete"
          );

          if (!committedWarning) return errors;

          successCallback({ hasPostCommitWarning: true });
          showToast({
            title: t`Changes saved`,
            description:
              committedWarning.message ??
              t`Your changes were saved, but some follow-up updates are still pending.`,
            semanticStyle: "warning",
          });
          return errors.filter((error) => error !== committedWarning);
        },
      });
    },
    [
      saveChangesToSingleSlotBooking,
      successCallback,
      membershipTypeId,
      patronLoyalty,
      store,
      showToast,
    ]
  );

  return [handleSaveSingleSlotBooking, isSavingChanges] as const;
};
const useSaveChangesToSingleSlotBookingString = graphql`
  mutation useSaveChangesToSingleSlotBookingMutation(
    $input: UpdateSingleSlotBookingInput!
    $membershipTypeId: ID!
    $bookingType: BookingType!
    $patronLoyalty: Boolean!
  ) {
    responsePayload: updateSingleSlotBooking(input: $input) {
      errors {
        code
        field
        message
      }
      success
      booking {
        id
        ...BookingSummary_booking @arguments(patronLoyalty: $patronLoyalty)
      }
    }
  }
`;
